import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { ValidSortProp, dataSorter } from 'utilities/dataSorters';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AdminParameterListItemModel } from 'types/admin/trackmanParameters';
import { DefaultRoutes } from 'Routes';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import { Link } from 'react-router-dom';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import dayjs from 'dayjs';
import { getLanguageShortText } from 'utilities/languages';
import { mapBooleanToString } from 'utilities/booleanMapper';
import { observer } from 'mobx-react-lite';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  name: string;
  primaryCategory: string;
  languages: string;
  visible: string;
  createdAt: string;
  updatedAt: string;
};

export const TrackmanParametersList = observer(() => {
  useSetPageTitle({ title: 'Trackman Parameters List', subtitle: '' });
  const ps = useStores().parametersStore;
  const [isModalOn, setIsModalOn] = useState(false);
  const [sortByProperty, setSortByProperty] = useState<ValidSortProp<AdminParameterListItemModel>>('title');
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');

  useEffect(() => {
    ps.parametersList.length === 0 && ps.getParameters();
    setSortByProperty('title');
    setSortOrder('ASCENDING');
  }, [ps]);

  useEffect(() => {
    let filteredList = ps.parametersList.filter((parameter) => parameter.title?.toLowerCase().includes(ps.searchValue.toLowerCase()));

    if (sortByProperty) {
      filteredList = dataSorter(filteredList, sortByProperty, sortOrder);
    }

    ps.filteredList = filteredList;
  }, [ps, ps.searchValue, sortByProperty, sortOrder]);

  const handleSortChange = (property: ValidSortProp<AdminParameterListItemModel>, order: 'ASCENDING' | 'DESCENDING') => {
    setSortByProperty(property);
    setSortOrder(order);
  };

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      ps.filteredList.map((parameter) => {
        return {
          id: parameter.id,
          name: parameter.title?.toLowerCase(),
          primaryCategory: parameter.primaryCategory,
          languages: parameter.languages ? parameter.languages.map((lng) => getLanguageShortText(lng)).join(', ') : '',
          visible: mapBooleanToString(parameter.visible),
          createdAt: dayjs(parameter.createdAt).format('MMM DD, YYYY'),
          updatedAt: dayjs(parameter.updatedAt).format('MMM DD, YYYY'),
        };
      }) as RowsDisplay[],
    [ps.filteredList]
  );

  const deleteParameter = useCallback(() => {
    ps.deleteParameterAndUpdate();
    setIsModalOn(false);
  }, [ps, setIsModalOn]);

  return (
    <div className='admin-content-container'>
      <div className='admin-list-header'>
        <HeaderSearchbar searchValue={ps.searchValue} onChange={ps.setSearchValue} />
        <HeaderCreateButton buttonTitle='New parameter' route={DefaultRoutes.TrackmanParametersEditor} />
      </div>

      <div className='tmu-table-wrapper'>
        <Table className='tmu-table admin-table'>
          <TableHead>
            <TableRow>
              <TableCell>
                Name
                <SortOrderButton
                  isActive={sortByProperty === 'title'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('title', sortOrder)}
                />
              </TableCell>
              <TableCell>Languages</TableCell>
              <TableCell>
                Primary Category
                <SortOrderButton
                  isActive={sortByProperty === 'primaryCategory'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('primaryCategory', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Visible
                <SortOrderButton
                  isActive={sortByProperty === 'visible'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('visible', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Date created
                <SortOrderButton
                  isActive={sortByProperty === 'createdAt'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('createdAt', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Last edited
                <SortOrderButton
                  isActive={sortByProperty === 'updatedAt'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('updatedAt', sortOrder)}
                />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsDisplay.length ? (
              rowsDisplay.map((row) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell className='languages'>{row.languages}</TableCell>
                    <TableCell>{row.primaryCategory}</TableCell>
                    <TableCell>{row.visible}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>{row.updatedAt}</TableCell>
                    <TableCell>
                      <div className='action-buttons'>
                        <Link to={`/Admin/${DefaultRoutes.TrackmanParametersEditor}/${row.id}`} className='row-button'>
                          <PencilIcon />
                        </Link>
                        <button
                          className={`row-button ${ps.isLoading ? 'disabled' : ''}`}
                          onClick={() => {
                            ps.selectedParameterId = row.id;
                            setIsModalOn(true);
                          }}
                          disabled={ps.isLoading}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Paragraph>
                    <>No parameters found</>
                  </Paragraph>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={deleteParameter} itemName='Trackman Parameter' />
    </div>
  );
});

export default TrackmanParametersList;
